import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { Userpilot } from 'userpilot';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useAuth0 } from '../lib/react-auth0-spa';

import { URLS, USER_GROUPS } from '../_constants';

export const ServicesEnum = {
  LaunchDarkly: 1,
  Contentful: 2,
  UserPilot: 3,
  Hubspot: 4,
  Datadog: 5,
};

export const useInitializationService = ({ companyInfo }) => {
  const { isAuthenticated, user, userGroups, isImpersonate, isCompanyAdmin } = useAuth0();
  const { hideAccessibeWidget, hideUserPilotWidget, hideHubspotWidget, recordUserSessions } = useFlags();
  const location = useLocation();
  const ldClient = useLDClient();
  const [statuses, setStatuses] = useState({
    [ServicesEnum.LaunchDarkly]: false, // +
    [ServicesEnum.Contentful]: false, // +
    [ServicesEnum.UserPilot]: false, // +
    [ServicesEnum.Hubspot]: false, // +
    [ServicesEnum.Datadog]: false, // +
  });

  const setServiceInitialized = useCallback((name, value = true) => {
    setStatuses((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // ----------------------LaunchDarkly-----------------------
  const [launchDarklyError, setLaunchDarklyError] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !companyInfo?.name) {
      return;
    }

    if (ldClient && !isEmpty(user) && !isEmpty(companyInfo)) {
      ldClient.identify({
        key: `${user.tenant_id}:${user.email}`,
        email: user.email,
        custom: {
          tenantId: user.tenant_id,
          userGroups,
          isImpersonate,
          companyName: user.tenant_id ? companyInfo?.name : null,
          companySubscriptions: user.tenant_id ? companyInfo?.subscriptions : [],
          createdAt: user.created_at,
          updatedAt: user.updated_at,
          loginCount: user.logins_count,
          blocked: user.blocked,
          region: companyInfo?.region,
          metadata: user.user_metadata,
        },
      });
      if (!statuses[ServicesEnum.LaunchDarkly]) {
        window.ldClient = ldClient;
        setServiceInitialized(ServicesEnum.LaunchDarkly, true);
      }
    }
  }, [ldClient, user, userGroups, companyInfo, isImpersonate, statuses]);

  // Log if launchDarkly blocked
  useEffect(() => {
    function onLaunchDarklyError() {
      setLaunchDarklyError(true);
      console.error(`LaunchDarkly error in company ID ${user.tenant_id} for user ${user.email}`);
    }

    if (ldClient) {
      ldClient.on('error', onLaunchDarklyError);
    }
    return () => ldClient?.off('error', onLaunchDarklyError);
  }, [ldClient]);
  // ----------------------LaunchDarkly-----------------------

  // ----------------------UserPilot-----------------------
  useEffect(() => {
    if (!window.location.href.includes('localhost') || process.env.REACT_APP_USERPILOT === 'true') {
      Userpilot.initialize(process.env.REACT_APP_USERPILOT_APP_TOKEN);
    }
  }, []);

  useEffect(() => {
    if (!statuses[ServicesEnum.LaunchDarkly] || !companyInfo?.name) {
      return;
    }

    if (
      !isEmpty(user) &&
      companyInfo &&
      (!window.location.href.includes('localhost') || process.env.REACT_APP_USERPILOT === 'true') &&
      !hideUserPilotWidget &&
      !statuses[ServicesEnum.UserPilot]
    ) {
      Userpilot.identify(`${user.email}`, {
        id: user.user_id,
        name: user.first_name ? user.name : user.nickname || user.email,
        email: user.email,
        userGroups,
        isImpersonate,
        created_at: user.created_at,
        updated_at: user.updated_at,
        login_count: user.logins_count,
        isBlocked: user.blocked,
        product: 'TRAINING',
        tenantId: user.tenant_id,
        company: {
          id: user.tenant_id,
          ...companyInfo,
        },
      });
      console.log(`UserPilot initialized`);
      setServiceInitialized(ServicesEnum.UserPilot, true);
    }
  }, [user, userGroups, companyInfo, isImpersonate, hideUserPilotWidget, statuses]);

  useEffect(() => {
    if (hideUserPilotWidget) {
      Userpilot.destroy();
    } else {
      Userpilot.reload();
    }
  }, [location, hideUserPilotWidget]);
  // ----------------------UserPilot-----------------------

  // ----------------------Accessibe-----------------------
  useEffect(() => {
    if (hideAccessibeWidget) {
      window.AccessiBe?.destroy && window.AccessiBe?.destroy();
      window.document.getElementById('acsb-trigger')?.remove();
      window.document.getElementById('acsb-widget')?.remove();
      const el1 = window.document.getElementsByClassName('acsb-trigger');
      const el2 = window.document.getElementsByClassName('acsb-widget');
      el1?.length && el1[0].remove();
      el2?.length && el2[0].remove();
    }
  }, [hideAccessibeWidget]);
  // ----------------------Accessibe-----------------------

  // ----------------------Hubspot-----------------------
  const [showHubspotWidget, setHubspotWidget] = useState();
  useEffect(() => {
    if (!statuses[ServicesEnum.LaunchDarkly]) {
      return;
    }

    if (
      userGroups.includes(USER_GROUPS.CUSTOMER_ADMIN) &&
      !(
        location.pathname === URLS.player ||
        location.pathname === URLS.dashboard ||
        location.pathname === URLS.assignment
      ) &&
      !statuses[ServicesEnum.Hubspot] &&
      !hideHubspotWidget
    ) {
      console.log(`Hubspot widget initialized`);
      setHubspotWidget(true);
      setServiceInitialized(ServicesEnum.Hubspot, true);
    } else if (
      location.pathname === URLS.player ||
      location.pathname === URLS.dashboard ||
      location.pathname === URLS.assignment ||
      location.pathname === URLS.teamsBase ||
      hideHubspotWidget
    ) {
      setHubspotWidget(false);
    }
  }, [hideHubspotWidget, location, userGroups, statuses]);
  // ----------------------Hubspot-----------------------

  // ----------------------Datadog-----------------------
  useEffect(() => {
    if (companyInfo?.name) {
      datadogLogs.init({
        clientToken: process.env.REACT_APP_DATADOG_CLIENT_ID,
        site: 'datadoghq.com',
        service: 'training-frontend',
        version: process.env.REACT_APP_VERSION,
        env: process.env.REACT_APP_DATADOG_ENV || 'dev',
        sessionSampleRate: 100,
        forwardErrorsToLogs: true,
        silentMultipleInit: true,
        trackInteractions: true,
        useCrossSiteSessionCookie: true,
        trackSessionAcrossSubdomains: true,
        beforeSend: (log) => {
          // Ignore noisy warnings
          if (
            log?.message?.includes('There is no feedback for the content id') ||
            log?.message?.includes('XHR error') ||
            log?.message?.includes('Failed prop') ||
            log?.message?.includes('Failed to fetch') ||
            log?.message?.includes('Fetch error head') ||
            log?.message?.includes('Warning:') ||
            log?.message?.includes('at e.exports') ||
            log?.message?.includes('Network Error') ||
            log?.message?.includes('Script error') ||
            log?.message?.includes('acsbJS is not defined') ||
            log?.message?.includes('ResizeObserver loop') ||
            log?.message?.includes('px.ads.linkedin.com') ||
            log?.message?.includes('pagead2.googlesyndication.com') ||
            log?.message?.includes('videojs is not a function') ||
            log?.message?.includes('Fullscreen is not supported') ||
            log?.message?.includes('requestFullscreen') ||
            log?.message?.includes('The object can not be found here') ||
            log?.message?.includes('undefined is not an object') ||
            log?.message?.includes('Malformed timestamp') ||
            log?.message?.includes('isStandalonePlayer') ||
            log?.message?.includes('https://cdn.acsbapp.com/') ||
            log?.message?.includes("'publicId' should be a string") ||
            log?.message?.includes('Session Complete') ||
            log?.message?.includes('Load failed') ||
            log?.message?.includes('Request aborted') ||
            log?.message?.includes('A disconnected element was passed') ||
            log?.message?.includes('Failed to send the request') ||
            log?.message?.includes('The node before which the new node') ||
            log?.message?.includes('livingsecurity.com/static/js') ||
            log?.message?.includes('XMLHttpRequest') ||
            log?.message?.includes('https://pagead2.googlesyndication.com/') ||
            log?.message?.includes('https://px.ads.linkedin.com/') ||
            log?.message?.includes('XHR error GET https://app.launchdarkly.com') ||
            log?.message?.includes('XHR error GET https://events.launchdarkly.com') ||
            log?.message?.includes('XHR error GET https://assets.livingsecurity.com')
          ) {
            return false;
          }
        },
      });
      datadogRum.init({
        applicationId: process.env.REACT_APP_DATADOG_APP_ID,
        clientToken: process.env.REACT_APP_DATADOG_CLIENT_ID,
        site: 'datadoghq.com',
        service: 'training-frontend',
        version: process.env.REACT_APP_VERSION,
        env: process.env.REACT_APP_DATADOG_ENV || 'dev',
        sessionSampleRate: isCompanyAdmin ? 100 : 0,
        sessionReplaySampleRate: isCompanyAdmin ? 100 : 0,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        silentMultipleInit: true,
        useCrossSiteSessionCookie: true,
        trackSessionAcrossSubdomains: true,
        enableExperimentalFeatures: ['feature_flags'],
        beforeSend: ({ error }) => {
          // Ignore noisy warnings
          if (
            error?.message?.includes('There is no feedback for the content id') ||
            error?.message?.includes('XHR error') ||
            error?.message?.includes('Failed prop') ||
            error?.message?.includes('Failed to fetch') ||
            error?.message?.includes('Fetch error head') ||
            error?.message?.includes('Warning:') ||
            error?.message?.includes('at e.exports') ||
            error?.message?.includes('Network Error') ||
            error?.message?.includes('Script error') ||
            error?.message?.includes('acsbJS is not defined') ||
            error?.message?.includes('ResizeObserver loop') ||
            error?.message?.includes('px.ads.linkedin.com') ||
            error?.message?.includes('pagead2.googlesyndication.com') ||
            error?.message?.includes('videojs is not a function') ||
            error?.message?.includes('Fullscreen is not supported') ||
            error?.message?.includes('requestFullscreen') ||
            error?.message?.includes('The object can not be found here') ||
            error?.message?.includes('undefined is not an object') ||
            error?.message?.includes('Malformed timestamp') ||
            error?.message?.includes('Load failed') ||
            error?.message?.includes('Request aborted') ||
            error?.message?.includes('A disconnected element was passed') ||
            error?.message?.includes('Failed to send the request') ||
            error?.message?.includes('The node before which the new node') ||
            error?.message?.includes('livingsecurity.com/static/js') ||
            error?.message?.includes('XMLHttpRequest') ||
            error?.message?.includes('https://cdn.acsbapp.com/') ||
            error?.message?.includes('https://pagead2.googlesyndication.com/') ||
            error?.message?.includes('https://px.ads.linkedin.com/') ||
            error?.message?.includes('XHR error GET https://app.launchdarkly.com') ||
            error?.message?.includes('XHR error GET https://events.launchdarkly.com') ||
            error?.message?.includes('XHR error GET https://assets.livingsecurity.com')
          ) {
            return false;
          }
        },
      });
      console.log(`Datadog initialized with app version ${process.env.REACT_APP_VERSION}`);

      if (user.email && companyInfo?.name) {
        console.log('Starting Platform session for: ', user.email, 'from', companyInfo?.name, '(', user.tenant_id, ')');
        datadogLogs.logger.debug(
          `Starting Platform session for: ${user.email} from ${companyInfo?.name} (${user.tenant_id})`,
        );
      }

      setServiceInitialized(ServicesEnum.Datadog, true);
    }
  }, [companyInfo]);

  useEffect(() => {
    if (!window.location.href.includes('localhost') && recordUserSessions) {
      if (isAuthenticated) {
        // Enabling Datadog Session Replay - https://docs.datadoghq.com/real_user_monitoring/guide/session-replay-getting-started
        datadogRum.startSessionReplayRecording();
      } else {
        datadogRum.stopSessionReplayRecording();
      }
    }
  }, [isAuthenticated, recordUserSessions]);
  // ----------------------Datadog-----------------------

  const isAllServicesInitialized = useMemo(() => {
    return Object.values(statuses).every((service) => service);
  }, [statuses]);

  return {
    showHubspotWidget,
    launchDarklyError,
    isAllServicesInitialized,
  };
};
